import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Empty, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import { ApiError, api, type TicketMessage, type TicketSummary, type TicketThread } from './api.js';
import { dateTime, relative } from './format.js';

/**
 * Support tickets, platform side.
 *
 * The difference from the merchant panel is not cosmetic: this side can write
 * an internal note, close a thread for good, and reassign or reprioritise one.
 * The merchant side can do none of those, and the server is what enforces it.
 */

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 5;

const STATUS_TONE: Record<string, 'ok' | 'pending' | 'warn' | 'neutral' | 'info'> = {
  OPEN: 'warn',
  AWAITING_ADMIN: 'warn',
  AWAITING_MERCHANT: 'info',
  RESOLVED: 'ok',
  CLOSED: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'ОТВОРЕН',
  AWAITING_ADMIN: 'ЧАКА НАС',
  AWAITING_MERCHANT: 'ЧАКА ТЪРГОВЕЦА',
  RESOLVED: 'РЕШЕН',
  CLOSED: 'ЗАТВОРЕН',
};

const CATEGORY_LABEL: Record<string, string> = {
  general: 'общ въпрос',
  payments: 'плащане',
  payouts: 'изплащания',
  disputes: 'спор',
  technical: 'техническо',
  account: 'профил',
  billing: 'такси',
};

const PRIORITY_LABEL: Record<string, string> = {
  low: 'нисък',
  normal: 'нормален',
  high: 'висок',
  urgent: 'спешен',
};

const PRIORITY_TONE: Record<string, 'neutral' | 'warn' | 'fail'> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'warn',
  urgent: 'fail',
};

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error(`Файлът ${file.name} не може да се прочете`));
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------

function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div>
      <input
        ref={input}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          e.target.value = '';
          const tooBig = list.find((f) => f.size > MAX_FILE_BYTES);
          if (tooBig) {
            setError(`${tooBig.name} е над 5 MB.`);
            return;
          }
          if (files.length + list.length > MAX_FILES) {
            setError(`Най-много ${MAX_FILES} файла на съобщение.`);
            return;
          }
          setError(null);
          onChange([...files, ...list]);
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="wc-btn wc-btn-sm wc-btn-secondary"
          onClick={() => input.current?.click()}
          disabled={disabled || files.length >= MAX_FILES}
        >
          Прикачи файл
        </button>
        <span style={{ fontSize: 12, color: 'var(--wc-faint)' }}>
          Снимки или PDF, до 5 MB всеки
        </span>
      </div>

      {files.length > 0 ? (
        <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ flex: 1, wordBreak: 'break-all' }}>{f.name}</span>
              <span style={{ color: 'var(--wc-faint)', fontSize: 12 }}>{bytes(f.size)}</span>
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                disabled={disabled}
              >
                Махни
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <div style={{ marginTop: 8 }}><Note tone="warn">{error}</Note></div> : null}
    </div>
  );
}

/**
 * Attachment bytes come from an authenticated endpoint, so the file is fetched
 * with the bearer token and handed to the browser as a blob rather than linked
 * to directly.
 */
function Attachments({ list }: { list: TicketMessage['attachments'] }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (list.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {list.map((a) => (
        <button
          key={a.id}
          type="button"
          className="wc-btn wc-btn-sm wc-btn-secondary"
          disabled={busy === a.id}
          title={`${a.filename} · ${bytes(a.size)}`}
          onClick={async () => {
            setBusy(a.id);
            try {
              const blob = await api.ticketAttachment(a.id);
              const url = URL.createObjectURL(blob);
              if (a.inline) {
                window.open(url, '_blank', 'noopener,noreferrer');
              } else {
                const link = document.createElement('a');
                link.href = url;
                link.download = a.filename;
                link.click();
              }
              setTimeout(() => URL.revokeObjectURL(url), 60_000);
            } finally {
              setBusy(null);
            }
          }}
        >
          {busy === a.id ? 'Отваряне…' : a.filename}
          <span style={{ color: 'var(--wc-faint)', marginLeft: 6, fontSize: 11 }}>{bytes(a.size)}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function AdminTicketsView() {
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.tickets({ status, search });
      setTickets(res.data);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [status, search]);

  useEffect(() => {
    void load();
  }, [load]);

  if (openId) {
    return (
      <AdminTicketThread
        id={openId}
        onBack={() => {
          setOpenId(null);
          void load();
        }}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note>
        Тук идват въпросите на търговците. Вътрешните бележки се виждат само от администрацията -
        търговецът никога не получава тяхното съдържание, защото филтърът е на сървъра, а не в
        интерфейса.
      </Note>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="wc-input"
          placeholder="Търси по номер или тема"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <div style={{ flex: 1 }} />
        {['all', 'AWAITING_ADMIN', 'OPEN', 'AWAITING_MERCHANT', 'RESOLVED', 'CLOSED'].map((s) => (
          <button
            key={s}
            type="button"
            className={`wc-btn wc-btn-sm ${status === s ? '' : 'wc-btn-secondary'}`}
            onClick={() => setStatus(s)}
          >
            {s === 'all' ? 'Всички' : (STATUS_LABEL[s] ?? s)}
          </button>
        ))}
      </div>

      {counts.open > 0 ? (
        <Note tone="warn" title={`Чакат отговор: ${counts.open}`}>
          Толкова тикета са отворени или чакат нашия отговор.
        </Note>
      ) : null}

      {error ? <Note tone="danger">{error}</Note> : null}

      {tickets === null ? (
        <Spinner label="Зареждане…" />
      ) : tickets.length === 0 ? (
        <Panel padded={false}>
          <Empty>Няма тикети.</Empty>
        </Panel>
      ) : (
        <Panel padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Търговец</th>
                  <th>Тема</th>
                  <th>Приоритет</th>
                  <th>Статус</th>
                  <th>Последно</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td className="wc-num" style={{ fontSize: 12.5 }}>{t.number}</td>
                    <td style={{ fontSize: 13 }}>{t.merchant?.name ?? '-'}</td>
                    <td>
                      <strong style={{ fontSize: 14 }}>{t.subject}</strong>
                      <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>
                        {CATEGORY_LABEL[t.category] ?? t.category} · {t.messages} съобщения
                      </div>
                    </td>
                    <td>
                      <Badge tone={PRIORITY_TONE[t.priority] ?? 'neutral'} icon={false}>
                        {PRIORITY_LABEL[t.priority] ?? t.priority}
                      </Badge>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[t.status] ?? 'neutral'}>
                        {STATUS_LABEL[t.status] ?? t.status}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 13 }} title={dateTime(t.last_message_at)}>
                      {relative(t.last_message_at)}
                    </td>
                    <td className="wc-right">
                      <button
                        type="button"
                        className="wc-btn wc-btn-sm wc-btn-secondary"
                        onClick={() => setOpenId(t.id)}
                      >
                        Отвори
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AdminTicketThread({ id, onBack }: { id: string; onBack: () => void }) {
  const [thread, setThread] = useState<TicketThread | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setThread(await api.ticket(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await toBase64(f) })),
      );
      await api.replyToTicket(id, { body, internal, attachments });
      setBody('');
      setFiles([]);
      setInternal(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function move(status: 'RESOLVED' | 'CLOSED' | 'OPEN'): Promise<void> {
    setBusy(true);
    try {
      await api.setTicketStatus(id, status);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function patch(change: { priority?: string; category?: string }): Promise<void> {
    setBusy(true);
    try {
      await api.updateTicket(id, change);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !thread) return <Note tone="danger">{error}</Note>;
  if (!thread) return <Spinner label="Зареждане…" />;

  const closed = thread.status === 'CLOSED';

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 900 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onBack}>
          ← Назад
        </button>
        <div style={{ flex: 1 }} />
        {!closed ? (
          <>
            {thread.status !== 'RESOLVED' ? (
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                onClick={() => void move('RESOLVED')}
                disabled={busy}
              >
                Отбележи като решен
              </button>
            ) : null}
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-danger"
              onClick={() => void move('CLOSED')}
              disabled={busy}
            >
              Затвори окончателно
            </button>
          </>
        ) : (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={() => void move('OPEN')}
            disabled={busy}
          >
            Отвори наново
          </button>
        )}
      </div>

      <Panel>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="wc-num" style={{ fontSize: 13 }}>{thread.number}</span>
          <strong style={{ fontSize: 16, flex: 1 }}>{thread.subject}</strong>
          <Badge tone={STATUS_TONE[thread.status] ?? 'neutral'}>
            {STATUS_LABEL[thread.status] ?? thread.status}
          </Badge>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)', marginTop: 4 }}>
          {thread.merchant?.name} · {thread.merchant?.email} · подаден {dateTime(thread.created_at)}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
            Приоритет{' '}
            <select
              className="wc-input"
              value={thread.priority}
              onChange={(e) => void patch({ priority: e.target.value })}
              disabled={busy}
              style={{ width: 'auto', display: 'inline-block', marginLeft: 6 }}
            >
              {Object.entries(PRIORITY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
            Категория{' '}
            <select
              className="wc-input"
              value={thread.category}
              onChange={(e) => void patch({ category: e.target.value })}
              disabled={busy}
              style={{ width: 'auto', display: 'inline-block', marginLeft: 6 }}
            >
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
        </div>
      </Panel>

      <div style={{ display: 'grid', gap: 12 }}>
        {thread.messages.map((m) => {
          if (m.author_side === 'SYSTEM') {
            return (
              <div key={m.id} style={{ fontSize: 12.5, color: 'var(--wc-muted)', textAlign: 'center' }}>
                {m.body} · {relative(m.created_at)}
              </div>
            );
          }
          const ours = m.author_side === 'ADMIN';
          return (
            <div
              key={m.id}
              style={{
                border: '1px solid var(--wc-border)',
                borderLeft: `3px solid ${
                  m.internal ? 'var(--wc-amber)' : ours ? 'var(--wc-blue)' : 'var(--wc-green)'
                }`,
                background: m.internal ? 'var(--wc-amber-tint)' : 'var(--wc-surface)',
                padding: '10px 14px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', marginBottom: 6 }}>
                <strong style={{ fontSize: 13.5 }}>
                  {m.author_name}{' '}
                  {m.internal ? <Badge tone="warn" icon={false}>ВЪТРЕШНА</Badge> : null}
                </strong>
                <span style={{ fontSize: 12, color: 'var(--wc-faint)' }} title={dateTime(m.created_at)}>
                  {relative(m.created_at)}
                </span>
              </div>
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
              <Attachments list={m.attachments} />
            </div>
          );
        })}
      </div>

      {closed ? (
        <Note>Тикетът е затворен. Отговор вече не може да се добави.</Note>
      ) : (
        <Panel title={internal ? 'Вътрешна бележка' : 'Отговор до търговеца'}>
          <textarea
            className="wc-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={20_000}
            placeholder={internal ? 'Вижда се само от администрацията' : 'Отговор, който търговецът ще получи'}
            style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
          />

          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, marginBottom: 12 }}
          >
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            Вътрешна бележка - търговецът няма да я види и тикетът няма да мине към него
          </label>

          <div style={{ marginBottom: 12 }}>
            <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />
          </div>

          {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

          <button
            type="button"
            className="wc-btn"
            onClick={send}
            disabled={busy || body.trim().length === 0}
          >
            {busy ? 'Изпращане…' : internal ? 'Запази бележката' : 'Изпрати отговора'}
          </button>
        </Panel>
      )}
    </div>
  );
}
